import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action } from '@ngrx/store';
import { AsyncState, createAsyncState } from '@ordersnproducts/utils/create-async-state';
import { createReducer, on } from '@ordersnproducts/utils/create-reducer';
import { CtdExperienceResponse } from '../../models/ctd-experience';
import { CtdOrderDetailResponse } from '../../models/ctd-order';
import OrderAction from '../actions/ctd-order.actions';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

export interface CtdOrder extends CtdOrderDetailResponse {
  id: string;
  experience?: CtdExperienceResponse;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface CtdOrderDispatches extends Partial<CtdOrder> {}

export type CtdOrderEntity = AsyncState<Partial<CtdOrder>>;
export interface CtdOrderState extends EntityState<CtdOrderEntity> {
  selectedCtdOrderId: string;
}

export const adapterCtdOrder = createEntityAdapter<CtdOrderEntity>({
  selectId: ({ payload }) => `${payload.id}`,
});

export const initialCtdOrderState = adapterCtdOrder.getInitialState({
  selectedCtdOrderId: null,
});

export const ctdOrderReducer = createReducer(
  initialCtdOrderState,

  on(OrderAction.initCtdOrder, (state, { id }) => adapterCtdOrder.addOne(createAsyncState({ id }), state)),

  on(OrderAction.updateCtdOrder, (state, { payload }) => {
    const hasEntity = _safe(() => !!state.entities[payload.id], false);

    if (hasEntity) {
      return {
        ...adapterCtdOrder.updateOne(
          {
            id: payload.id,
            changes: { payload: { ...state.entities[payload.id].payload, ...payload } },
          },
          state,
        ),
      };
    }

    return adapterCtdOrder.addOne(createAsyncState({ ...payload }), state);
  }),

  on(OrderAction.selectCtdOrder, (state, { id }) => {
    return {
      ...state,
      selectedCtdOrderId: id,
    };
  }),

  on(OrderAction.fetchCompletedCtdOrder, (state, { payload }) =>
    adapterCtdOrder.updateOne(
      {
        id: payload.id,
        changes: createAsyncState({ ...state.entities[payload.id].payload, ...payload }, { isFetched: true }),
      },
      state,
    ),
  ),

  on(
    OrderAction.fetchCtdOrder,
    OrderAction.fetchCtdOrderConfirmed,
    OrderAction.fetchCtdOrderReleased,
    (state, { id }) =>
      adapterCtdOrder.updateOne(
        {
          id,
          changes: createAsyncState({ ...state.entities[id].payload }, { ...state.entities[id], isLoading: true }),
        },
        state,
      ),
  ),

  on(
    OrderAction.fetchCanceledCtdOrder,
    OrderAction.fetchCanceledCtdOrderConfirmed,
    OrderAction.fetchCanceledCtdOrderReleased,
    (state, { id }) =>
      adapterCtdOrder.updateOne(
        { id, changes: createAsyncState({ ...state.entities[id].payload }, { ...state.entities[id], hasError: true }) },
        state,
      ),
  ),
);

export function reducer(state: CtdOrderState, action: Action) {
  return ctdOrderReducer(state, action);
}

export const selectSelectedEntityId = (state: CtdOrderState) => state.selectedCtdOrderId;
export const { selectIds, selectEntities, selectAll, selectTotal } = adapterCtdOrder.getSelectors();
